import type { ComputedRef } from "vue";
import { computed } from "vue";
import { useLocationStore } from "../LocationStore/useLocationStore";

export interface ResourceScope {
  location: string | null
  client: string | null
}

export const LOGIN_SCOPE: ResourceScope = { location: null, client: null };

export function toIriRecord<T extends { "@id": string }>(items: T[]): Record<string, T> {
  return Object.fromEntries(items.map(item => [item["@id"], item]));
}

export function useResourceScope(): ComputedRef<ResourceScope> {
  const locationStore = useLocationStore();
  return computed(() => ({
    location: locationStore.modeSelectedLocation.value,
    client: locationStore.modeSelectedClient.value,
  }));
}

function resource(name: string) {
  return {
    root: [name] as const,
    all: (scope: ResourceScope) => [name, "all", scope] as const,
    list: (scope: ResourceScope) => [name, "list", scope] as const,
    stats: (scope: ResourceScope, paramsKey: string) => [name, "stats", scope, paramsKey] as const,
  };
}

export type ResourceKeyFactory = ReturnType<typeof resource>;

export const resourceKeys = {
  tasks: resource("tasks"),
  tourProgresses: resource("tourProgresses"),
  reports: resource("reports"),
  sorts: resource("sorts"),
  properties: resource("properties"),
  propertyGroups: resource("propertyGroups"),
  propertyRoutes: resource("propertyRoutes"),
  products: resource("products"),
  labels: resource("labels"),
  reportTopics: resource("reportTopics"),
  users: resource("users"),
  systemUsers: resource("systemUsers"),
  userGroups: resource("userGroups"),
  clients: resource("clients"),
  clientRoles: resource("clientRoles"),
  accessInvitations: resource("accessInvitations"),
  locations: resource("locations"),
  spaces: resource("spaces"),
  apps: resource("apps"),
  appSubscriptions: resource("appSubscriptions"),
  appCredentials: resource("appCredentials"),
  developerAccessRequests: resource("developerAccessRequests"),
  appSubscriptionImportBatches: resource("appSubscriptionImportBatches"),
  warehouses: resource("warehouses"),
  reservations: resource("reservations"),
  generalLedgers: resource("generalLedgers"),
  oauthScopes: resource("oauthScopes"),
  permissionTemplateGroups: resource("permissionTemplateGroups"),
  automations: resource("automations"),
  automationTemplates: resource("automationTemplates"),
  automationEvents: resource("automationEvents"),
  automationEventLogs: resource("automationEventLogs"),
  emailEventLogs: resource("emailEventLogs"),
  devices: resource("devices"),
  deviceActions: resource("deviceActions"),
  descriptionHints: resource("descriptionHints"),
  timeEntries: resource("timeEntries"),
  usedProducts: resource("usedProducts"),
  invoices: resource("invoices"),
  notifications: resource("notifications"),
  inventoryMovements: resource("inventoryMovements"),
  inventoryStock: resource("inventoryStock"),
  inventoryForecast: resource("inventoryForecast"),
  activities: resource("activities"),
  taskViews: resource("taskViews"),
  reportViews: resource("reportViews"),
  pins: resource("pins"),
  trafficLogs: resource("trafficLogs"),
  clientSettings: resource("clientSettings"),
  permissions: resource("permissions"),
  sessions: resource("sessions"),
  connectedApplications: resource("connectedApplications"),
  auditEvents: resource("auditEvents"),
  mediaObjects: resource("mediaObjects"),
  priceQuotes: resource("priceQuotes"),
  usage: resource("usage"),
  taskTemplates: resource("taskTemplates"),
  automationDateRules: resource("automationDateRules"),
  appSubscriptionLocationSettings: resource("appSubscriptionLocationSettings"),
  automationTemplateInstances: resource("automationTemplateInstances"),
  topics: resource("topics"),
  locales: resource("locales"),
  payloadFields: resource("payloadFields"),
  whatsAppConversations: resource("whatsAppConversations"),
  whatsAppMessages: resource("whatsAppMessages"),
  whatsAppPhoneNumbers: resource("whatsAppPhoneNumbers"),
  translationContexts: resource("translationContexts"),
  userPreferences: resource("userPreferences"),
};

export function itemKey(iri: string) {
  return ["item", iri] as const;
}
